// Defer Google Analytics (gtag.js) until the browser is idle or the user first
// interacts. nuxt-gtag is configured with `enabled: false` (see nuxt.config.ts),
// so its client plugin sets up window.dataLayer and buffers the js/config
// commands but does NOT inject gtag.js. We load the script here via
// useGtag().initialize(), keeping ~90 KB of third-party JS off the critical
// path (and out of the Total Blocking Time / INP window) while still loading GA
// well before any real navigation away from the page.
//
// Because the js/config commands are already buffered on window.dataLayer, and
// trackEvent() (composables/analytics.ts) pushes further events there too,
// nothing is lost by deferring: gtag.js flushes the whole queue once it loads.
export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  // No measurement id configured (e.g. local builds) → nothing to load.
  if (!config.public?.gtag?.id) return

  const { initialize } = useGtag()

  let started = false
  const events = ['click', 'keydown', 'touchstart', 'scroll'] as const

  const load = () => {
    try {
      // initialize() injects the script via useHead, which needs an active Nuxt
      // instance — restore it since we're running in a deferred callback.
      nuxtApp.runWithContext(() => initialize())
    } catch {
      // Analytics is best-effort — never let a GA load failure surface as a
      // page error.
    }
  }

  // `afterPaint` only on the interaction path: there it runs *inside* the
  // click/keydown that triggered it, and injecting the tag there puts a head
  // patch plus ~90 KB of script parsing between the click and the frame that
  // answers it — the first interaction on a page measured consistently slower
  // than every one after it. Waiting for that frame costs GA nothing. The idle
  // path loads straight away instead, because a page opened in a background tab
  // gets idle callbacks but never an animation frame.
  const start = (afterPaint: boolean) => {
    if (started) return
    started = true
    events.forEach((e) => window.removeEventListener(e, onInteraction))
    if (afterPaint && typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => setTimeout(load, 0))
    } else {
      load()
    }
  }

  const onInteraction = () => start(true)
  const onIdle = () => start(false)

  // Load on the first meaningful interaction…
  events.forEach((e) =>
    window.addEventListener(e, onInteraction, { passive: true, once: true }),
  )

  // …or when the main thread goes idle, whichever comes first.
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(onIdle, { timeout: 4000 })
  } else {
    setTimeout(onIdle, 3500)
  }
})
